import React from 'react';
import Backdrop from '@material-ui/core/Backdrop';
import Box from '@material-ui/core/Box';
import MuiButton from '@material-ui/core/Button';
import Dialog from '@material-ui/core/Dialog';
import DialogContent from '@material-ui/core/DialogContent';
import Paper from '@material-ui/core/Paper';
import withStyles from '@material-ui/core/styles/withStyles';
import Typography from '@material-ui/core/Typography';
import PropTypes from 'prop-types';

import { segmentTracking } from '~/functions/SegmentFunctions';

import { PersonContext } from '../../contexts/PersonContext';
import { create } from '../../feathersWrapper';
import { asyncSetState } from '../../functions/InputHandlers';
import history from '../../history';
import Button from '../Button';
import SubscribeDialog from '../SubscribeDialog';

import BuyNowPromoModal from './BuyNowPromoModal';

const styles = (theme) => ({
  tooltip: {
    position: 'relative',
    width: '100%',
    zIndex: theme.zIndex.drawer + 2,
    marginTop: '-50px',
  },
  arrow: {
    zIndex: theme.zIndex.drawer + 3,
    position: 'relative',
    textAlign: 'center',
    top: '-1px',
    marginBottom: '-16px',
  },
  backdrop: {
    zIndex: theme.zIndex.drawer + 1,
  },
  tourImage: {
    marginBottom: theme.spacing(1),
    width: '100%',
  },
  tourVideo: {
    marginBottom: theme.spacing(1),
    width: '100%',
    height: 'auto',
  },
  checks: {
    marginRight: '16px',
    color: theme.palette.primary.dark,
  },
  price: {
    color: theme.palette.primary.dark,
    fontWeight: 'bold',
    marginBottom: '16px',
  },
  regularPrice: {
    color: theme.palette.action.active,
    textDecoration: 'line-through',
  },
});

class WelcomeTour extends React.PureComponent {
  constructor(props) {
    super(props);

    this.state = {
      runTour: true,
      stepIndex: 0,
      subscribeDialog: false,
    };
  }

  async componentDidMount() {
    const { organizationId, isProduction } = this.context;
    const unitCount = await create(this, 'reports', {
      organizationId,
      reportName: 'unitCount',
    });

    const discount = 60;
    let planSelect;

    const planOptions = [
      {
        name: 'Essentials',
        price: 15.0,
        discountPrice: 15.0 * (1.0 - discount / 100.0),
        units: 'Up to 3 units',
        maxUnits: 3,
        billing: 'Billed monthly',
        id: 'essential',
      },
      {
        name: 'Growth',
        price: 25.0,
        discountPrice: 25.0 * (1.0 - discount / 100.0),
        units: 'Up to 10 units',
        maxUnits: 10,
        billing: 'Billed monthly',
        id: 'growth',
      },
      {
        name: 'Investor',
        price: 45.0,
        discountPrice: 45.0 * (1.0 - discount / 100.0),
        units: 'Up to 20 units',
        maxUnits: 20,
        billing: 'Billed monthly',
        id: 'investor',
      },
      {
        name: 'Professional',
        price: 80.0,
        discountPrice: 80.0 * (1.0 - discount / 100.0),
        units: 'Unlimited units',
        billing: 'Billed monthly',
        id: 'professional',
      },
    ];

    if (unitCount > 20) {
      planSelect = planOptions[3];
    } else if (unitCount > 10) {
      planSelect = planOptions[2];
    } else if (unitCount > 3) {
      planSelect = planOptions[1];
    } else {
      planSelect = planOptions[0];
    }

    this.handleSegmentTrack('tour loaded', 'Welcome Loaded');

    await asyncSetState(
      {
        run: true,
        unitCount,
        discount,
        planSelect,
        allowSkips: !isProduction,
      },
      this,
    );
    setTimeout(() => {
      document.activeElement.blur();
    }, 100);
  }

  onWelcomeSubscribed = async () => {
    const { organizationId, setOrganizationId } = this.context;
    this.setState({ planSelect: null });
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      event: 'onboardingSubscribed',
      organizationId,
    });
    await setOrganizationId(organizationId);
    this.closeTour();
  };

  handleSegmentTrack = (event, location) => {
    const { organizationId, showWelcome, partnerName, adminLogin, settings } = this.context;

    return segmentTracking(
      event,
      `Onboarding Tour - ${location}`,
      {
        organizationId,
        showWelcome,
        partnerName,
        adminLogin,
      },
      {
        '2_variant_test': settings['2_variant_test'],
      },
    );
  };

  getStep = (index) => {
    const { partnerName, promptPayment } = this.context;
    const { classes } = this.props;
    const { planSelect, allowSkips } = this.state;

    let stepArray = [
      {
        isDialog: true,
        url: '/dashboard',
        content: () => (
          <DialogContent>
            <Box mx="auto" textAlign="center">
              <img className={classes.tourImage} src="/tour_welcome_dashboard.png" alt="Welcome" />
              <Typography variant="h5" gutterBottom>
                {partnerName === 'TurboTenant' ? 'Welcome to TurboTenant Accounting 👋' : 'Welcome to REI Hub 👋'}
              </Typography>
            </Box>
            <Box mb={1} textAlign="center">
              <Typography variant="body1" color="textSecondary">
                {`Built for real estate investors — get ready to breeze through tax season and maximize your deductions.
              No spreadsheets. No stress.`}
              </Typography>
            </Box>
            <Box pb={1}>
              <Box maxWidth="400px" marginX="auto" textAlign="center" mt={4} px={3}>
                <Box marginBottom={1}>
                  <Button
                    hasFullWidth
                    size="large"
                    variant="contained"
                    onClick={() => {
                      this.handleSegmentTrack('start_tour clicked', 'Welcome');
                      this.nextStep();
                    }}
                  >
                    Start Tour
                  </Button>
                </Box>
                {allowSkips && (
                  <MuiButton
                    onClick={() => {
                      this.handleSegmentTrack('skip_tour clicked', 'Welcome');
                      this.skipTour();
                    }}
                    size="large"
                    color="primary"
                  >
                    Skip Tour
                  </MuiButton>
                )}
              </Box>
            </Box>
          </DialogContent>
        ),
      },
      {
        url: '/properties',
        isDialog: true,
        content: () => (
          <DialogContent>
            <Box mx="auto" textAlign="center">
              <video src="/tour_adding_a_property.mp4" autoPlay loop muted className={classes.tourVideo} />
              <Typography variant="h5" gutterBottom>
                Organize by property
              </Typography>
            </Box>
            <Box mb={1} textAlign="center">
              <Typography variant="body1" color="textSecondary">
                {`Add your portfolio to categorize income and expenses by property — so your reports stay organized
                and your Schedule E tax forms are ready when you are.`}
              </Typography>
            </Box>
            <Box pb={1}>
              <Box maxWidth="400px" marginX="auto" textAlign="center" mt={4} px={3}>
                <Box marginBottom={1}>
                  <Button
                    hasFullWidth
                    size="large"
                    variant="contained"
                    onClick={() => {
                      this.handleSegmentTrack('next clicked', 'Properties');
                      this.nextStep();
                    }}
                  >
                    Next
                  </Button>
                </Box>
                {allowSkips && (
                  <MuiButton
                    onClick={() => {
                      this.handleSegmentTrack('skip_tour clicked', 'Properties');
                      this.skipTour();
                    }}
                    size="large"
                    color="primary"
                  >
                    Skip Tour
                  </MuiButton>
                )}
              </Box>
            </Box>
          </DialogContent>
        ),
      },
      {
        url: '/accounts/banking',
        isDialog: true,
        content: () => (
          <DialogContent>
            <Box mx="auto" textAlign="center">
              <video src="/tour_adding_an_account.mp4" autoPlay loop muted className={classes.tourVideo} />
              <Typography variant="h5" gutterBottom>
                Automatically import transactions
              </Typography>
            </Box>
            <Box mb={1} textAlign="center">
              <Typography variant="body1" color="textSecondary">
                {`Securely link to your accounts —
                so you spend less time entering data and more time growing your rental business.`}
              </Typography>
            </Box>
            <Box pb={1}>
              <Box maxWidth="400px" marginX="auto" textAlign="center" mt={4} px={3}>
                <Box marginBottom={1}>
                  <Button
                    hasFullWidth
                    size="large"
                    variant="contained"
                    onClick={() => {
                      this.handleSegmentTrack('next clicked', 'Accounts');
                      this.nextStep();
                    }}
                  >
                    Next
                  </Button>
                </Box>
                {allowSkips && (
                  <MuiButton
                    onClick={() => {
                      this.handleSegmentTrack('skip_tour clicked', 'Accounts');
                      this.skipTour();
                    }}
                    size="large"
                    color="primary"
                  >
                    Skip Tour
                  </MuiButton>
                )}
              </Box>
            </Box>
          </DialogContent>
        ),
      },
      {
        url: '/transactions/imported',
        isDialog: true,
        content: () => (
          <DialogContent>
            <Box mx="auto" textAlign="center">
              <video src="/tour_import_feed.mp4" autoPlay loop muted className={classes.tourVideo} />
              <Typography variant="h5" gutterBottom>
                Bookkeeping in half the time
              </Typography>
            </Box>
            <Box mb={1} textAlign="center">
              <Typography variant="body1" color="textSecondary">
                Smart automations and built-in templates make bookkeeping easier and more accurate.
              </Typography>
            </Box>
            <Box pb={1}>
              <Box maxWidth="400px" marginX="auto" textAlign="center" mt={4} px={3}>
                <Box marginBottom={1}>
                  <Button
                    hasFullWidth
                    size="large"
                    variant="contained"
                    onClick={() => {
                      this.handleSegmentTrack('next clicked', 'Import Feed');
                      this.nextStep();
                    }}
                  >
                    Next
                  </Button>
                </Box>
                {allowSkips && (
                  <MuiButton
                    onClick={() => {
                      this.handleSegmentTrack('skip_tour clicked', 'Import Feed');
                      this.skipTour();
                    }}
                    size="large"
                    color="primary"
                  >
                    Skip Tour
                  </MuiButton>
                )}
              </Box>
            </Box>
          </DialogContent>
        ),
      },
      {
        url: '/accounts/loans',
        isDialog: true,
        content: () => (
          <DialogContent>
            <Box mx="auto" textAlign="center">
              <video src="/tour_adding_a_loan.mp4" autoPlay loop muted className={classes.tourVideo} />
              <Typography variant="h5" gutterBottom>
                Properly split loan payments
              </Typography>
            </Box>
            <Box mb={1} textAlign="center">
              <Typography variant="body1" color="textSecondary">
                Automatically amortize principal and interest so your deductions and cashflow stay accurate as your
                equity grows.
              </Typography>
            </Box>
            <Box pb={1}>
              <Box maxWidth="400px" marginX="auto" textAlign="center" mt={4} px={3}>
                <Box marginBottom={1}>
                  <Button
                    hasFullWidth
                    size="large"
                    variant="contained"
                    onClick={() => {
                      this.handleSegmentTrack('next clicked', 'Loans');
                      this.nextStep();
                    }}
                  >
                    Next
                  </Button>
                </Box>
                {allowSkips && (
                  <MuiButton
                    onClick={() => {
                      this.handleSegmentTrack('skip_tour clicked', 'Loans');
                      this.skipTour();
                    }}
                    size="large"
                    color="primary"
                  >
                    Skip Tour
                  </MuiButton>
                )}
              </Box>
            </Box>
          </DialogContent>
        ),
      },
      {
        url: '/reports',
        isDialog: true,
        content: () => (
          <DialogContent>
            <Box mx="auto" textAlign="center">
              <video src="/tour_reports.mp4" autoPlay loop muted className={classes.tourVideo} />
              <Typography variant="h5" gutterBottom>
                One-click tax exports & reports
              </Typography>
            </Box>
            <Box mb={1} textAlign="center">
              <Typography variant="body1" color="textSecondary">
                {`Easily generate a complete tax packet for your CPA —
                plus unlock insights that help you spot trends, grow smarter, and stay investor-ready.`}
              </Typography>
            </Box>
            <Box pb={2}>
              <Box maxWidth="400px" marginX="auto" textAlign="center" mt={4} px={3}>
                <Button
                  hasFullWidth
                  size="large"
                  variant="contained"
                  onClick={() => {
                    this.handleSegmentTrack('get_started clicked', 'Reports');
                    this.nextStep();
                  }}
                >
                  Get Started
                </Button>
              </Box>
            </Box>
          </DialogContent>
        ),
      },
    ];

    if (promptPayment && planSelect) {
      stepArray.push({
        isDialog: true,
        skip: true,
        url: '/dashboard',
        content: () => (
          <BuyNowPromoModal
            onBuyNow={() => this.setState({ subscribeDialog: true })}
            // onTryForFree={() => this.nextStep()}
            onTryForFree={() => this.closeTour()}
          />
        ),
      });
    }

    if (index === 'skip') {
      const skipStepIndex = stepArray.findIndex((step) => step.skip === true);
      if (skipStepIndex === -1) {
        this.closeTour();
        return;
      }
      const nextStep = stepArray[skipStepIndex];
      history.push(nextStep.url);
      this.setState({ stepIndex: skipStepIndex });
      return;
    }
    if (index >= stepArray.length) {
      this.closeTour();
      return;
    }
    return stepArray[index];
  };

  nextStep = async () => {
    const { stepIndex } = this.state;
    const nextStep = this.getStep(stepIndex + 1);

    if (nextStep) {
      await history.push(nextStep.url);
      this.setState({ stepIndex: stepIndex + 1 });
    }
  };

  skipTour = () => {
    this.getStep('skip');
  };

  closeTour = async () => {
    const { setIsTourVisible } = this.context;
    setIsTourVisible(false);
    this.setState({ runTour: false });
    history.push('/get-started');
  };

  closeTourFeed = async () => {
    const { setIsTourVisible } = this.context;
    setIsTourVisible(false);
    this.setState({ runTour: false });
    history.push('/accounts/banking/import-feed');
  };

  render() {
    const { runTour, stepIndex, subscribeDialog } = this.state;
    const { classes } = this.props;
    const step = this.getStep(stepIndex);

    if (!runTour) {
      return null;
    }

    if (subscribeDialog) {
      return (
        <SubscribeDialog
          isOpen
          welcomeOffer
          closeDialog={() => {
            this.setState({ subscribeDialog: false });
          }}
          onWelcomeSubscribed={this.onWelcomeSubscribed}
        />
      );
    }

    if (step.isDialog) {
      return (
        <Dialog open scroll="body" maxWidth="sm" fullWidth disableBackdropClick disableEscapeKeyDown>
          {step.content()}
        </Dialog>
      );
    }

    return (
      <Box>
        <Backdrop className={classes.backdrop} open />
        <Paper className={classes.tooltip}>{step.content()}</Paper>
        <div className={classes.arrow}>
          <svg width="32" height="16" version="1.1" xmlns="http://www.w3.org/2000/svg">
            <polygon points="0,0,16 16,32 0" fill="#fff" />
          </svg>
        </div>
      </Box>
    );
  }
}

WelcomeTour.contextType = PersonContext;

WelcomeTour.propTypes = {
  classes: PropTypes.objectOf(PropTypes.string).isRequired,
};

export default withStyles(styles)(WelcomeTour);
